import FeeTypesEnum from '../../../Enums/FeeTypesEnum';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import DataSourceEnum from '../../../Enums/DataSourceEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import selectHelper from '../../../Helpers/selectHelper';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import FreelancerFeeItem from '../../../Domain/FreelancerFeeItem';
import FreelancerFeeService from '../../../Services/FreelancerFeeService';

export default class EditFeeWindowDescription {
  // Getter (not an eager static field): FreelancerFeeItem <-> FreelancerUserAdmin form an import
  // cycle, so referencing FreelancerFeeItem at class-definition time throws a TDZ ReferenceError.
  static get itemType() {
    return FreelancerFeeItem;
  }

  static titleForEdit = (item) => `${item.FreelancerUserName} - Edit Vendor Fee (${item.FreelancerFeeId}) `;
  static titleForCreate = (item) =>
    `${item.FreelancerUserName} - Clone Vendor Fee (original fee: ${item.OriginalFreelancerFeeId}) `;
  static finishButtonLabel = 'Save Fee';
  static messageOnSuccess = 'Fee has been saved.';

  // Old EditFeePanel posted straight to EditCloneFreelancerFee with no pre-save rules (unlike the
  // Add flow, which runs FreelancerFeeItem.applyRules first) - preserved as-is. MUST return the
  // promise (wizard contract). EditCloneFreelancerFee itself branches on FreelancerFeeId > 0
  // between PUT (edit) and POST (create, used here for clone).
  static serviceFunction = (edited) => FreelancerFeeService.EditCloneFreelancerFee(edited);

  // --- Host-side seeding, mirrors old EditFeePanel's constructor (getTempItem) + componentDidMount ---
  // (role-flag fetch for the Fee Type options moved out to FreelancerFeeAdmin - see report/comments there)

  // Edit: identity copy, plus the two UI-only fields the backend item never carries:
  // Source (derived from whether the fee has a TpSurveyId) and UseOptFee (derived from OptFee).
  static buildItemForEdit(selectedItem) {
    return {
      ...selectedItem,
      Source: selectedItem.TpSurveyId ? DataSourceEnum.ThirdParty : DataSourceEnum.Apollo,
      UseOptFee: parseFloat(selectedItem.OptFee) >= 0,
    };
  }

  // Clone: fresh id, Start reset to today, End pushed to indefinite if it already lapsed.
  // OriginalFreelancerFeeId is carried along only so the wizard title can show
  // "original fee: <id>" the way the old panel did (via props.editedItem.FreelancerFeeId).
  static buildItemForClone(selectedItem) {
    const endDate =
      dateTimeHelper.getDateFromDateString(selectedItem.EndDate) < new Date()
        ? dateTimeHelper.indefiniteDateValue()
        : selectedItem.EndDate;
    return {
      ...selectedItem,
      OriginalFreelancerFeeId: selectedItem.FreelancerFeeId,
      FreelancerFeeId: '',
      StartDate: new Date(),
      EndDate: endDate,
      Source: selectedItem.TpSurveyId ? DataSourceEnum.ThirdParty : DataSourceEnum.Apollo,
      UseOptFee: parseFloat(selectedItem.OptFee) >= 0,
    };
  }

  static isTimebased = (item) => item.TimebasedSettlementEnabled === true;
  static isDataQuality = (item) => item.FeeType === FeeTypesEnum.DataQuality;
  static isThirdParty = (item) => item.Source === DataSourceEnum.ThirdParty;

  static pagesInGeneralEditWizzard = [
    {
      label: 'General',
      fields: [
        {
          fieldName: 'FreelancerUserId',
          label: 'Vendor Id',
          className: 'col-12',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FreelancerUserName',
          label: 'Vendor Name',
          className: 'col-12',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FeeType',
          label: 'Fee Type',
          className: 'col-12',
          type: InputTypesEnum.Select,
          selectvalues: (item) => selectHelper.getSelectOptionsForFeeType(item),
          disableClearButton: true,
          validityChecker: ValidityHelper.forRequiredSelectField,
        },
        {
          fieldName: 'TimebasedSettlementEnabled',
          label: 'Timebased Settlement',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          validityChecker: EditFeeWindowDescription.validityCheckerForTimebased,
        },
        {
          fieldName: 'CountryId',
          label: 'Country',
          className: 'col-12',
          type: InputTypesEnum.CountrySelect,
          hide: EditFeeWindowDescription.isTimebased,
        },
        {
          fieldName: 'SelfAssigned',
          label: 'Self Assigned',
          className: 'col-12',
          type: InputTypesEnum.Bool,
          hide: EditFeeWindowDescription.isTimebased,
          disabled: (item) => item.FeeType !== FeeTypesEnum.Resource,
        },
        {
          fieldName: 'SurveyTypeCallsEmails',
          label: 'Calls Emails',
          className: 'col-12',
          type: InputTypesEnum.Bool,
          hide: EditFeeWindowDescription.isTimebased,
          disabled: (item) => item.FeeType !== FeeTypesEnum.Resource,
        },
        {
          fieldName: 'StartDate',
          label: 'Start',
          className: 'col-12',
          type: InputTypesEnum.Date,
          validityChecker: EditFeeWindowDescription.validityCheckerForDates,
        },
        {
          fieldName: 'EndDate',
          label: 'End',
          className: 'col-12',
          type: InputTypesEnum.DateWithIndefinite,
          validityChecker: EditFeeWindowDescription.validityCheckerForDates,
        },
        {
          fieldName: 'Fee',
          label: (item) =>
            `Fee (${item.FreelancerUserCurrencyCode})` + (item.TimebasedSettlementEnabled ? '/hour' : ''),
          className: 'col-12',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: EditFeeWindowDescription.validityCheckerForFee,
        },
        {
          fieldName: 'Tier',
          label: 'Tier',
          className: 'col-12',
          type: InputTypesEnum.Select,
          selectvalues: () => FreelancerFeeItem.getTierSelectOptions(),
          hide: (item) => !EditFeeWindowDescription.isDataQuality(item),
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'UseOptFee',
          label: 'Use Opt Fee',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          hide: (item) => !EditFeeWindowDescription.isDataQuality(item),
          onFieldChange: EditFeeWindowDescription.onUseOptFeeChange,
        },
        {
          fieldName: 'OptFee',
          label: (item) => `Opt Fee (${item.FreelancerUserCurrencyCode})`,
          className: 'col-12',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          hide: (item) => !EditFeeWindowDescription.isDataQuality(item) || parseFloat(item.OptFee) < 0,
          info: 'Opt Fee is only applied if the starting status is Reviewed.',
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'ClientId',
          label: 'Client',
          className: 'col-12',
          type: InputTypesEnum.ClientSelect,
          validityChecker: EditFeeWindowDescription.validityCheckerForClient,
          onFieldChange: EditFeeWindowDescription.onClientChange,
        },
        {
          fieldName: 'Source',
          label: 'Source',
          className: 'col-12',
          type: InputTypesEnum.SourceSelect,
        },
        {
          fieldName: 'SurveyId',
          label: 'Survey',
          className: 'col-12',
          type: InputTypesEnum.SurveySelect,
          hide: EditFeeWindowDescription.isThirdParty,
          disabled: (item) => !item.ClientId,
          idFilter: 'ClientId',
        },
        {
          fieldName: 'TpSurveyId',
          label: 'TpSurvey',
          className: 'col-12',
          type: InputTypesEnum.TpSurveySelect,
          hide: (item) => !EditFeeWindowDescription.isThirdParty(item),
          disabled: (item) => !item.ClientId,
          idFilter: 'ClientId',
        },
        {
          fieldName: 'WaveId',
          label: 'Wave',
          className: 'col-12',
          type: InputTypesEnum.WaveSelect,
          hide: (item) =>
            EditFeeWindowDescription.isTimebased(item) || EditFeeWindowDescription.isThirdParty(item),
          disabled: (item) => !(parseInt(item.ClientId) > 0),
          idFilter: 'ClientId',
        },
      ],
    },
  ];

  static onUseOptFeeChange(item) {
    // Enabling starts the opt fee at 0; disabling marks it unused (-1) so it posts as null.
    item.OptFee = item.UseOptFee ? (parseFloat(item.OptFee) >= 0 ? item.OptFee : '0') : '-1';
  }

  static onClientChange(item) {
    // Client drives the survey/wave filters, so clear the dependent selections when it changes.
    item.SurveyId = '';
    item.TpSurveyId = '';
    item.WaveId = '';
    item.TpWaveId = '';
  }

  static validityCheckerForTimebased(e, item) {
    if (
      !item.TimebasedSettlementEnabled &&
      item.FeeType !== FeeTypesEnum.Resource &&
      item.FeeType !== FeeTypesEnum.DataQuality
    ) {
      return 'In case of this fee type Timebased Settlement has to be enabled.';
    }
    if (item.TimebasedSettlementEnabled && !item.FreelancerUserTimebasedSettlementEnabled) {
      return 'Allow the time-based settlement on the vendor profile.';
    }
    return '';
  }

  static validityCheckerForFee(e, item) {
    return item.TimebasedSettlementEnabled
      ? ValidityHelper.forRequiredNumberFieldGreaterThanZero(item.Fee)
      : ValidityHelper.forRequiredNumberFieldAtLeastZero(item.Fee);
  }

  static validityCheckerForDates(e, item) {
    return item.StartDate > item.EndDate ? 'The End Date has to be later than the Start Date.' : '';
  }

  static validityCheckerForClient(e, item) {
    if (item.FeeType !== FeeTypesEnum.DataQuality || item.TimebasedSettlementEnabled === true) {
      return '';
    }
    const intval = parseInt(e);
    if (isNaN(intval) || intval < 1) {
      return 'If the FeeType is DataQuality, a client has to be choosen.';
    }
    return '';
  }
}
